import { useParams, useNavigate, useRouter } from '@tanstack/react-router';
import { Card } from '@/components';
import { IconButton, Button, LoadingIcon } from '@/primer/components';
import { useCampaign, useClaimCampaign } from '@/api';
import { PageColumn } from '@/components/layout';

// State badge colours using Primer status tokens
const stateColors: Record<string, string> = {
  invited: 'bg-informative text-informative',
  participating: 'bg-success text-success',
  completed: 'bg-disabled text-bodylighter',
  claimed: 'bg-warning text-warning',
};

// Task state to determine if completed
const isTaskCompleted = (state: string | null): boolean => {
  return state === 'completed' || state === 'approved';
};

export function CampaignDetail() {
  const { id: campaignId } = useParams({ from: '/_authenticated/campaign/$id' });
  const navigate = useNavigate();
  const router = useRouter();

  const { data: campaign, isLoading, error } = useCampaign(campaignId);
  const claimMutation = useClaimCampaign();

  const handleClaim = async () => {
    try {
      await claimMutation.mutateAsync({
        campaignId,
        request: {}, // Empty request for basic claim
      });
      navigate({ to: '/settings/address' });
    } catch (err) {
      console.error('Failed to claim campaign:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="col-span-12 flex justify-center py-8">
        <LoadingIcon size="xl" />
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <PageColumn className="text-center py-8">
        <Card>
          <h1 className="header-h2 text-error mb-4">Campaign Not Found</h1>
          <Button onClick={() => navigate({ to: '/dashboard' })}>Back to Dashboard</Button>
        </Card>
      </PageColumn>
    );
  }

  // Get the first product for display (if available)
  const primaryProduct = campaign.products?.[0];
  const displayImage = campaign.imageUrl || primaryProduct?.productImageUrl;
  const displayName = campaign.name || primaryProduct?.productName || 'Campaign';
  const campaignState = campaign.state || 'invited';
  const stateColorClass = stateColors[campaignState] || stateColors.invited;

  return (
    <PageColumn className="py-4">
      {/* Back Button — mobile only */}
      <div className="mb-4 md:hidden">
        <IconButton
          icon="chevron-left"
          onClick={() => router.history.back()}
          aria-label="Go back"
        />
      </div>

      {/* Campaign Image */}
      {displayImage && (
        <img
          src={displayImage}
          alt={displayName}
          className="w-full h-[16rem] object-cover rounded-large mb-6"
        />
      )}

      {/* Campaign Info */}
      <div className="mb-6">
        <Card>
          <div className="flex items-start justify-between mb-4">
            <div>
              <h1 className="header-h1 text-header">{displayName}</h1>
              <p className="body-small text-bodylighter">{campaign.campaignType}</p>
            </div>
            <span className={`px-3 py-1 rounded-rounded body-small font-medium ${stateColorClass}`}>
              {campaignState.charAt(0).toUpperCase() + campaignState.slice(1)}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <p className="body-small text-bodylighter">Tasks</p>
              <p className="header-h2 text-header">
                {campaign.completedTaskCount} / {campaign.totalTaskCount}
              </p>
            </div>
            {campaign.expired && (
              <div>
                <p className="body-small text-bodylighter">Expires</p>
                <p className="header-h2 text-header">
                  {new Date(campaign.expired).toLocaleDateString()}
                </p>
              </div>
            )}
          </div>

          {campaign.description && (
            <div className="border-t border-default pt-4">
              <h3 className="header-h3 text-header mb-2">Description</h3>
              <p className="body-medium text-body">{campaign.description}</p>
            </div>
          )}

          {/* Products Section */}
          {campaign.products && campaign.products.length > 0 && (
            <div className="border-t border-default pt-4 mt-4">
              <h3 className="header-h3 text-header mb-2">Products</h3>
              <div className="space-y-3">
                {campaign.products.map((product) => (
                  <div key={product.casProductId} className="flex items-center space-x-3">
                    {product.productImageUrl && (
                      <img
                        src={product.productImageUrl}
                        alt={product.productName || 'Product'}
                        className="w-12 h-12 object-cover rounded-small"
                      />
                    )}
                    <div>
                      <p className="body-medium text-header">{product.productName}</p>
                      {product.productBrand && (
                        <p className="body-small text-bodylighter">{product.productBrand}</p>
                      )}
                      {product.fairMarketValue && (
                        <p className="body-small text-interactive">
                          ${product.fairMarketValue} {product.fairMarketValueCurrency}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tasks Section */}
          {campaign.tasks && campaign.tasks.length > 0 && (
            <div className="border-t border-default pt-4 mt-4">
              <h3 className="header-h3 text-header mb-2">Tasks</h3>
              <ul className="space-y-2">
                {campaign.tasks.map((task) => {
                  const completed = isTaskCompleted(task.state);
                  return (
                    <li key={task.id} className="flex items-start">
                      <span className={`mr-2 ${completed ? 'text-success' : 'text-bodylighter'}`}>
                        {completed ? '✓' : '○'}
                      </span>
                      <div className={completed ? 'text-bodylighter' : ''}>
                        <span className="body-medium capitalize">
                          {task.type.replace(/_/g, ' ')}
                        </span>
                        {task.required && (
                          <span className="ml-2 body-xs text-error">(Required)</span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Terms Section */}
          {campaign.terms && (
            <div className="border-t border-default pt-4 mt-4">
              <h3 className="header-h3 text-header mb-2">Terms & Conditions</h3>
              <p className="body-small text-body">{campaign.terms}</p>
            </div>
          )}
        </Card>
      </div>
      {campaignState === 'invited' && (
        <Button fullWidth size="large" onClick={handleClaim} isLoading={claimMutation.isPending}>
          Claim This Campaign
        </Button>
      )}

      {claimMutation.error && (
        <p className="body-small text-error text-center mt-4">
          Failed to claim campaign. Please try again.
        </p>
      )}
    </PageColumn>
  );
}

export default CampaignDetail;
